/* Client-side telemetry — ships errors/failed requests to /api/client-log.
 * Fire-and-forget; never blocks the UI and never throws back.
 *
 * Each outbound event is enriched with:
 *  - console_mirror   (last 60 s of console.error / console.warn)
 *  - route_history    (last 20 client-side route changes)
 *  - session_snapshot (viewport, online, visibility, timing, tz, lang)
 *  - app_meta         (build SHA / env from REACT_APP_* vars)
 *  - request_headers / request_body / response_headers
 *    (set by api.js response interceptor on axios failures; redacted).
 */
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BASE}/api/client-log`;

// ---- In-tab ring buffer of shipped events ----------------------------------
const RING_MAX = 50;
const ring = [];

function _pushRing(entry) {
  ring.push(entry);
  if (ring.length > RING_MAX) ring.shift();
}

export function getRing() {
  return ring.slice();
}

// ---- Console mirror --------------------------------------------------------
const CONSOLE_WINDOW_MS = 60_000;
const CONSOLE_MAX = 100;
const consoleBuf = [];

function _safeStringifyArg(a) {
  if (a == null) return String(a);
  if (typeof a === "string") return a;
  if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ""}`;
  try {
    return JSON.stringify(a, (_k, v) => (v instanceof Error ? `${v.name}: ${v.message}` : v));
  } catch {
    try { return String(a); } catch { return "[unserializable]"; }
  }
}

function _pushConsole(level, args) {
  const now = Date.now();
  consoleBuf.push({
    ts: new Date(now).toISOString(),
    level,
    args: args.map(_safeStringifyArg).map((s) => String(s).slice(0, 2000)),
  });
  while (consoleBuf.length > CONSOLE_MAX) consoleBuf.shift();
  const cutoff = now - CONSOLE_WINDOW_MS;
  while (consoleBuf.length && new Date(consoleBuf[0].ts).getTime() < cutoff) {
    consoleBuf.shift();
  }
}

export function getConsoleMirror() {
  const cutoff = Date.now() - CONSOLE_WINDOW_MS;
  return consoleBuf.filter((e) => new Date(e.ts).getTime() >= cutoff);
}

function installConsoleMirror() {
  if (typeof window === "undefined") return;
  if (window.__e1ConsoleMirrorInstalled) return;
  window.__e1ConsoleMirrorInstalled = true;
  for (const lvl of ["error", "warn"]) {
    const original = console[lvl].bind(console);
    console[lvl] = (...args) => {
      try { _pushConsole(lvl, args); } catch { /* never break console */ }
      original(...args);
    };
  }
}

// ---- Route history tracker --------------------------------------------------
const ROUTE_MAX = 20;
const routeBuf = [];

function _pushRoute() {
  try {
    const path = window.location.pathname + window.location.search;
    const last = routeBuf[routeBuf.length - 1];
    if (last && last.path === path) return; // dedupe consecutive duplicates
    routeBuf.push({ ts: new Date().toISOString(), path });
    while (routeBuf.length > ROUTE_MAX) routeBuf.shift();
  } catch { /* ignore */ }
}

export function getRouteHistory() {
  return routeBuf.slice();
}

function installRouteHistoryTracker() {
  if (typeof window === "undefined") return;
  if (window.__e1RouteTrackerInstalled) return;
  window.__e1RouteTrackerInstalled = true;

  const origPush = window.history.pushState;
  const origReplace = window.history.replaceState;
  window.history.pushState = function (...args) {
    const r = origPush.apply(this, args);
    queueMicrotask(_pushRoute);
    return r;
  };
  window.history.replaceState = function (...args) {
    const r = origReplace.apply(this, args);
    queueMicrotask(_pushRoute);
    return r;
  };
  window.addEventListener("popstate", _pushRoute);
  _pushRoute(); // seed with initial path
}

// ---- Session snapshot -------------------------------------------------------
function _sessionStart() {
  if (typeof window === "undefined") return Date.now();
  if (!window.__e1SessionStart) window.__e1SessionStart = Date.now();
  return window.__e1SessionStart;
}

export function snapshotSession() {
  if (typeof window === "undefined") return null;
  let page_load_ms = null;
  try {
    const nav = (performance.getEntriesByType?.("navigation") || [])[0];
    if (nav) page_load_ms = Math.round(nav.loadEventEnd - nav.startTime);
  } catch { /* ignore */ }
  return {
    path: window.location.pathname + window.location.search,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    visibility: typeof document !== "undefined" ? document.visibilityState : null,
    lang: typeof navigator !== "undefined" ? navigator.language : null,
    tz_offset_min: new Date().getTimezoneOffset(),
    session_age_ms: Date.now() - _sessionStart(),
    page_load_ms,
  };
}

// ---- App metadata -----------------------------------------------------------
export function getAppMeta() {
  return {
    build_sha: process.env.REACT_APP_GIT_SHA || null,
    build_env: process.env.REACT_APP_ENV || null,
    app_version: process.env.REACT_APP_VERSION || null,
  };
}

// ---- Header redaction -------------------------------------------------------
const SENSITIVE_HEADER_SUBSTR = ["authorization", "cookie", "token", "key", "secret", "api-key"];

export function redactHeaders(h) {
  if (!h || typeof h !== "object") return {};
  const src = typeof h.toJSON === "function" ? h.toJSON() : h;
  const out = {};
  for (const [k, v] of Object.entries(src || {})) {
    const lk = String(k).toLowerCase();
    if (SENSITIVE_HEADER_SUBSTR.some((s) => lk.includes(s))) {
      out[k] = "[redacted]";
    } else {
      out[k] = typeof v === "string" ? v : JSON.stringify(v);
    }
  }
  return out;
}

// ---- Noise filter -----------------------------------------------------------
// Pre-auth pages (/login, /forgot-password, /reset-password, /accept-invite, /)
// legitimately hit `/api/auth/me` and `/api/workspace` on mount to detect a
// stale session. A 401 there is EXPECTED — not a bug — and shipping it as a
// telemetry error drowned the actual bugs. Suppress those specific events so
// the admin log UI only shows real failures.
const _PREAUTH_PATHS = ["/login", "/forgot-password", "/reset-password", "/accept-invite", "/"];
const _PREAUTH_401_URLS = ["/api/auth/me", "/api/workspace"];
function _isBenignPreAuth401(payload) {
  if (payload?.status !== 401) return false;
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  if (!_PREAUTH_PATHS.includes(page)) return false;
  const url = String(payload?.url || "");
  return _PREAUTH_401_URLS.some((p) => url.includes(p));
}

// ---- Outbound shipper -------------------------------------------------------
export function shipClientEvent(payload) {
  if (_isBenignPreAuth401(payload)) return;  // silent-drop auth-probe 401s
  _sessionStart(); // lazy-init
  const body = {
    level: payload.level || "error",
    source: payload.source || "client",
    message: String(payload.message || "").slice(0, 4000),
    stack: payload.stack ? String(payload.stack).slice(0, 8000) : null,
    url: payload.url ? String(payload.url).slice(0, 1000) : null,
    method: payload.method || null,
    status: payload.status ?? null,
    duration_ms: payload.duration_ms ?? null,
    response_body: payload.response_body
      ? String(payload.response_body).slice(0, 4000)
      : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    page: typeof window !== "undefined" ? window.location.pathname : null,
    request_headers: payload.request_headers || null,
    request_body: payload.request_body ? String(payload.request_body).slice(0, 4000) : null,
    response_headers: payload.response_headers || null,
    console_mirror: getConsoleMirror(),
    route_history: getRouteHistory(),
    session_snapshot: snapshotSession(),
    app_meta: getAppMeta(),
  };
  _pushRing({ ...body, ts: new Date().toISOString() });
  try {
    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    // Bare axios — no interceptors → telemetry can't error-loop.
    axios.post(ENDPOINT, body, { headers, timeout: 5000 }).catch(() => {});
  } catch { /* ignore */ }
}

/* Install global window-level handlers + console/route trackers. Call once from index.js. */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  installConsoleMirror();
  installRouteHistoryTracker();
  if (window.__e1TelemetryInstalled) return;
  window.__e1TelemetryInstalled = true;

  window.addEventListener("error", (event) => {
    shipClientEvent({
      source: "window",
      message: event?.message || "window error",
      stack: event?.error?.stack || null,
      url: event?.filename || null,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    shipClientEvent({
      source: "unhandledrejection",
      message: String(reason?.message || reason || "unhandled rejection"),
      stack: reason?.stack || null,
    });
  });
}
